import { call, CallEffect, PutEffect, select } from 'redux-saga/effects';
import { getCurrentFlagId, getCustomer, ResponseCardValidation, ResponseEncryptedCard, ResponseFlags } from './functions';
import { automaticApi } from '@/settings/api';
import apiPCI from '@/settings/apiPCI';
import { clearString, getFormattedDeviceFingerPrintID } from '@/utils/functions';
import { ReducersTypes } from '@/redux/reducers/Models';
import { Action } from 'redux-saga';
import { AxiosResponse } from 'axios';
import environment from '@/settings/environment';
import { PAYMENT_TYPE } from '@/redux/ducks/customer';

export interface Fetch {
    visitorId: string;
    card: {
      number: string;
      name: string;
      expiration: string;
      autoPayment?: boolean;
    };
    callback: (success: boolean, code?: string) => void;
  }
  
  export interface ChangePaymentMethodAction {
    type: string;
    payload: Fetch;
  }

const uriPci = '/api/v1/tokenizing';
const uriCardValidate = '/account/v1/validateCredictCard';
const uriFlags = '/dominios/v1/financeiro/bandeiras';
const uri = '/account/v1/paymentMethod';

export default function* changePaymentMethod({
    payload,
}: ChangePaymentMethodAction): Generator<
    CallEffect<unknown> | PutEffect<Action>,
    void,
    AxiosResponse
> {
    try {
        // @ts-expect-error
        const customer: ReducersTypes['customerReducer'] = yield select(getCustomer);
        const client = customer?.client;
        const account = customer?.account;

        const card = payload.card;

        const journey = `APP-${environment?.api?.prefix?.toUpperCase() ?? ""}${account?.paymentType === PAYMENT_TYPE.IS_CARD ? "AlteracaoDadosCartao" : "AlteracaoFormaPagamento"}`;

        const deviceFingerprintID = getFormattedDeviceFingerPrintID(payload.visitorId ?? "");

        const responseEncryptedCard: ResponseEncryptedCard = yield call(
            apiPCI.post,
            uriPci,
            {
                dataToToken: clearString(card?.number),
            }
        );

        const encryptedData = responseEncryptedCard?.data;

        const responseFlags: ResponseFlags = yield call(automaticApi.get, uriFlags);

        const currentFlagId = getCurrentFlagId(responseFlags?.data?.listaBandeiraCartao, encryptedData?.additionalInformation?.brand?.name);

        const address = client?.addresses?.find(address => address?.main) ?? client?.addresses?.[0];

        const responseCardValidation: ResponseCardValidation = yield call(
            automaticApi.post,
            uriCardValidate,
            {
                deviceFingerprintID,
                financialData: {
                    value: "0.00",
                    creditCard: {
                        flag: currentFlagId,
                        cardNumber: encryptedData?.token,
                        monthDueDate: card?.expiration?.split("/")[0],
                        yearDueDate: card?.expiration?.split("/")[1]
                    },
                },
                client: {
                    codeClient: client?.id,
                    cpf: client?.document,
                    phone: client?.phone,
                    name: client?.name?.split(" ")[0],
                    lastName: client?.name?.split(" ")?.[1],
                    email: client?.email,
                    registrationDate: client?.registrationDate,
                    address: {
                        street: `${address?.number}, ${address?.streetName}`,
                        state: address?.state?.name!,
                        country: "BR",
                        city: address?.city,
                        zipCode: address?.zipcode
                    }
                },
                journey,
            }
        );

        const dataCardValidation = responseCardValidation?.data?.data;
    
        
        const body = {
            paymentModality: payload.card.autoPayment ? "2" : "4", 
            paymentForm: "1",
            accountDueDay: "1",
            invoiceDeliveryMethod: "0",
            flagAuthorizesNFE: true,
            flagChangesModality: true,
            deviceFingerprintID,
            creditCard: {
                flagCode: currentFlagId,
                expirationMonth: card?.expiration?.split("/")[0],
                expirationYear: card?.expiration?.split("/")[1],
                cardNumber: encryptedData?.token,
                validationToken: dataCardValidation.validationToken
            },
            journey,
        };

        const response: {
            data: {
                data: {
                    protocol: string;
                };
                status: {
                    codigoRetorno: number | string;
                    mensagem: string;
                };
            };
        } = yield call(automaticApi.put, uri, body);

        if (response?.data?.status?.codigoRetorno === 0 || response?.data?.status?.codigoRetorno === '-100') {
            yield call(
                payload.callback,
                true
            );
        } else {
            yield call(
                payload.callback,
                false
            );
        }
    } catch (error) {
        yield call(
            payload.callback,
            false
        );
    }
}
